import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCookieBite,
  faShieldAlt,
  faChartBar,
  faBullhorn,
  faCog,
  faCheck,
  faTimes,
  faSliders,
} from '@fortawesome/free-solid-svg-icons';
import { cookiesAPI } from '../services/api';
import '../styles/CookieConsent.css';

/* ══════════════════════════════════════════════════════════════
   Cookie Consent — GDPR-compliant real cookie management
   
   Categories:
   ├─ necessary    Always on. Auth tokens, CSRF, security, consent.
   ├─ functional   Theme, language, UI state, calendar prefs.
   ├─ analytics    First-party telemetry, anonymous usage data.
   └─ marketing    Third-party tracking pixels, ads (future).
   
   Storage:
   • Consent is stored in a real cookie (__eq_cc) + localStorage backup
   • Cookie max-age: 365 days (re-consent annually)
   • Consent choice gates all non-essential cookie/storage writes
   ══════════════════════════════════════════════════════════════ */

export interface CookiePreferences {
  necessary: boolean;    // Always true — cannot be toggled off
  functional: boolean;   // Theme, language, tutorial state, calendar
  analytics: boolean;    // Telemetry, session tracking
  marketing: boolean;    // Third-party ad scripts (future)
}

const COOKIE_NAME = '__eq_cc';
const COOKIE_VERSION = '2';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds
const LS_KEY = '__eq_cookie_consent';

// ─── Real Cookie Helpers ──────────────────────────────────────

function setRealCookie(name: string, value: string, maxAge: number): void {
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `path=/`,
    `max-age=${maxAge}`,
    `SameSite=Lax`,
  ];
  if (window.location.protocol === 'https:') {
    parts.push('Secure');
  }
  document.cookie = parts.join('; ');
}

function getRealCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function deleteRealCookie(name: string): void {
  document.cookie = `${name}=; path=/; max-age=0`;
}

/** Remove non-essential cookies when user revokes consent */
function purgeNonEssentialCookies(prefs: CookiePreferences): void {
  const cookies = document.cookie.split(';').map(c => c.trim().split('=')[0]);
  const essential = new Set([COOKIE_NAME, 'eq_sid', 'eq_csrf', 'eq_consent', 'XSRF-TOKEN', 'csrf_token']);
  const analyticsCookies = ['_ga', '_gid', '_gat', '__eq_sid', 'eq_vid', 'eq_sc'];
  const marketingCookies = ['_fbp', '_fbc', 'IDE', 'NID', 'fr', 'g_csrf_token', 'g_state'];
  const preferenceCookies = ['eq_theme', 'eq_lang'];

  cookies.forEach(name => {
    if (!name || essential.has(name)) return;
    if (!prefs.analytics && analyticsCookies.includes(name)) deleteRealCookie(name);
    if (!prefs.marketing && marketingCookies.includes(name)) deleteRealCookie(name);
    if (!prefs.functional && preferenceCookies.includes(name)) deleteRealCookie(name);
  });

  if (!prefs.analytics) {
    try { sessionStorage.removeItem('__eq_sid'); } catch {}
  }
}

/** Remove functional localStorage keys if functional consent revoked */
function cleanFunctionalStorage(allowed: boolean): void {
  if (allowed) return;
  const keys = [
    'app_theme', 'app_language', 'reduceMotion', 'prefersTouch',
    'elvoaq_tutorial_done', 'elvoaq_tutorial_seen',
    'elvoaq_calendar_categories', 'dashboard_v2', 'workspace_favorites',
  ];
  keys.forEach(key => { try { localStorage.removeItem(key); } catch {} });
}

// ─── Save / Load Consent ──────────────────────────────────────

function savePrefs(prefs: CookiePreferences): void {
  const payload = JSON.stringify({
    v: COOKIE_VERSION,
    n: 1,
    f: prefs.functional ? 1 : 0,
    a: prefs.analytics ? 1 : 0,
    m: prefs.marketing ? 1 : 0,
    ts: Date.now(),
  });

  // 1. Save client-side (real cookie + localStorage backup)
  setRealCookie(COOKIE_NAME, payload, COOKIE_MAX_AGE);
  try { localStorage.setItem(LS_KEY, payload); } catch {}

  // 2. Sync consent to backend (sets server-side eq_consent cookie)
  cookiesAPI.saveConsent({
    analytics: prefs.analytics,
    preferences: prefs.functional,
    marketing: prefs.marketing,
  }).catch(() => {
    // Silent fail — client-side consent is the source of truth
  });

  purgeNonEssentialCookies(prefs);
  cleanFunctionalStorage(prefs.functional);
  window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: prefs }));
}

function loadPrefs(): CookiePreferences | null {
  try {
    let raw = getRealCookie(COOKIE_NAME);
    if (!raw) raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Migrate v1 → v2: v1 had no "functional" field
    if (parsed.v === '1') {
      return { necessary: true, functional: true, analytics: !!parsed.a, marketing: !!parsed.m };
    }
    if (parsed.v !== COOKIE_VERSION) return null;
    return {
      necessary: true,
      functional: !!parsed.f,
      analytics: !!parsed.a,
      marketing: !!parsed.m,
    };
  } catch {
    return null;
  }
}

// ─── Public API ───────────────────────────────────────────────

export function getCookiePreferences(): CookiePreferences {
  return loadPrefs() || { necessary: true, functional: true, analytics: true, marketing: false };
}

export function hasCookieConsent(): boolean {
  return loadPrefs() !== null;
}

/** Check if a specific category is allowed */
export function isCategoryAllowed(cat: keyof CookiePreferences): boolean {
  if (cat === 'necessary') return true;
  const prefs = loadPrefs();
  if (!prefs) return false;
  return prefs[cat];
}

/** Guard for functional localStorage writes */
export function canWriteFunctional(): boolean {
  const prefs = loadPrefs();
  // Before consent, allow functional (default-on category)
  if (!prefs) return true;
  return prefs.functional;
}

/** Manage third-party script loading based on consent */
export function applyScriptGating(prefs: CookiePreferences): void {
  if (!prefs.marketing) {
    // Remove Google Identity script if marketing not consented
    const gs = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
    if (gs) gs.remove();
    deleteRealCookie('g_csrf_token');
    deleteRealCookie('g_state');

    // Remove Apple Sign-In script
    const as = document.querySelector('script[src*="appleid.cdn-apple.com"]');
    if (as) as.remove();
  }
}

// ─── Category Config ──────────────────────────────────────────

const CATEGORIES = [
  {
    key: 'necessary' as const,
    icon: faShieldAlt,
    iconClass: 'cc-cat-icon--necessary',
    title: 'Strictly Necessary',
    desc: 'Essential for the site to function. Includes session management, CSRF protection, and this consent record. Cannot be disabled.',
    cookies: ['Session cookie (eq_sid)', 'CSRF token (eq_csrf)', 'Consent cookie (__eq_cc)', 'JWT backup token'],
    alwaysOn: true,
  },
  {
    key: 'functional' as const,
    icon: faSliders,
    iconClass: 'cc-cat-icon--functional',
    title: 'Functional',
    desc: 'Remember your preferences like theme, language, dashboard layout, and tutorial progress. Without these, settings reset on each visit.',
    cookies: ['Theme (eq_theme)', 'Language (eq_lang)', 'Dashboard layout', 'Tutorial state'],
    alwaysOn: false,
  },
  {
    key: 'analytics' as const,
    icon: faChartBar,
    iconClass: 'cc-cat-icon--analytics',
    title: 'Analytics & Performance',
    desc: 'Help us understand how you use the platform so we can improve it. All data is anonymized. Includes visitor tracking and page views.',
    cookies: ['Visitor ID (eq_vid)', 'Session count (eq_sc)', 'Page view events', 'Feature usage counters'],
    alwaysOn: false,
  },
  {
    key: 'marketing' as const,
    icon: faBullhorn,
    iconClass: 'cc-cat-icon--marketing',
    title: 'Third-Party & Social',
    desc: 'Enables Google Sign-In, Apple Sign-In, and future third-party integrations. These services may set their own cookies.',
    cookies: ['Google Identity (g_state)', 'Apple Auth session'],
    alwaysOn: false,
  },
];

/* ═══════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════ */

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>({
    necessary: true,
    functional: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    const saved = loadPrefs();
    if (!saved) {
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
    applyScriptGating(saved);
  }, []);

  // Listen for "open cookie settings" from footer button
  useEffect(() => {
    const openSettings = () => {
      const saved = loadPrefs();
      if (saved) setPrefs(saved);
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener('cookie-settings:open', openSettings);
    return () => window.removeEventListener('cookie-settings:open', openSettings);
  }, []);

  const handleSave = useCallback((p: CookiePreferences) => {
    const final = { ...p, necessary: true };
    setPrefs(final);
    savePrefs(final);
    applyScriptGating(final);
    setVisible(false);
    setShowDetails(false);
  }, []);

  const acceptAll = useCallback(() => {
    handleSave({ necessary: true, functional: true, analytics: true, marketing: true });
  }, [handleSave]);

  const acceptEssentialOnly = useCallback(() => {
    handleSave({ necessary: true, functional: false, analytics: false, marketing: false });
  }, [handleSave]);

  if (!visible) return null;

  return (
    <div className="cc-overlay" role="dialog" aria-label="Cookie preferences">
      <div className={`cc-banner${showDetails ? ' cc-banner--expanded' : ''}`}>
        {/* Header */}
        <div className="cc-header">
          <div className="cc-icon">
            <FontAwesomeIcon icon={faCookieBite} />
          </div>
          <div className="cc-header-text">
            <h3>Cookie Preferences</h3>
            <p>
              We use cookies and local storage to keep you logged in, remember your settings,
              and improve our platform. You choose what's allowed.
            </p>
          </div>
        </div>

        {/* Detail panel */}
        {showDetails && (
          <div className="cc-details">
            {CATEGORIES.map(cat => (
              <div className="cc-category" key={cat.key}>
                <div className="cc-category-head">
                  <span className={`cc-cat-icon ${cat.iconClass}`}>
                    <FontAwesomeIcon icon={cat.icon} />
                  </span>
                  <div className="cc-cat-info">
                    <h4>{cat.title}</h4>
                    <p>{cat.desc}</p>
                    <div className="cc-cookie-list">
                      {cat.cookies.map((c, i) => (
                        <span key={i} className="cc-cookie-tag">{c}</span>
                      ))}
                    </div>
                  </div>
                  {cat.alwaysOn ? (
                    <span className="cc-always-on">Always On</span>
                  ) : (
                    <button
                      role="switch"
                      aria-checked={prefs[cat.key]}
                      className={`cc-toggle${prefs[cat.key] ? ' cc-toggle--on' : ''}`}
                      onClick={() => setPrefs(p => ({ ...p, [cat.key]: !p[cat.key] }))}
                      aria-label={`Toggle ${cat.title} cookies`}
                    >
                      <span className="cc-toggle-track">
                        <span className="cc-toggle-label cc-toggle-label--off">OFF</span>
                        <span className="cc-toggle-label cc-toggle-label--on">ON</span>
                      </span>
                      <span className="cc-toggle-knob" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="cc-actions">
          {!showDetails ? (
            <>
              <button className="cc-btn cc-btn--manage" onClick={() => setShowDetails(true)}>
                <FontAwesomeIcon icon={faCog} /> Customize
              </button>
              <button className="cc-btn cc-btn--reject" onClick={acceptEssentialOnly}>
                <FontAwesomeIcon icon={faTimes} /> Essential Only
              </button>
              <button className="cc-btn cc-btn--accept" onClick={acceptAll}>
                <FontAwesomeIcon icon={faCheck} /> Accept All
              </button>
            </>
          ) : (
            <>
              <button className="cc-btn cc-btn--reject" onClick={acceptEssentialOnly}>
                Essential Only
              </button>
              <button className="cc-btn cc-btn--accept" onClick={() => handleSave(prefs)}>
                <FontAwesomeIcon icon={faCheck} /> Save My Choices
              </button>
            </>
          )}
        </div>

        <div className="cc-footer-links">
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <span>·</span>
          <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          <span>·</span>
          <span className="cc-eu-badge">🇪🇺 GDPR Compliant</span>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
